import { cloneDeep } from 'lodash'
import { CommUser } from 'src/types/communication'
import { Contact, ContactSettings } from 'src/types/contact'
import { HTTP } from '../lib/axios'
import { getContactById } from './common'

const getInitials = (displayName: string, contactType: string) => {
  let initials = ''
  const splitName = displayName.split(' ').filter((n) => n)
  initials = splitName[0].charAt(0)
  const hasSplitNames = splitName.length > 1
  const isCompany = contactType === 'Company'

  if (hasSplitNames && !isCompany) {
    initials += splitName[1].charAt(0)
  } else if (isCompany) {
    initials += splitName[0].charAt(1)
  }
  return initials
}

export const getContactInitials = (
  commContact: Array<CommUser>,
  userId: string,
  contactList: Contact[]
): string | null => {
  let initials = null
  if (!commContact || !commContact.length) return initials
  const contact = commContact.find((c) => c.type === 'Contact')

  if (contact) {
    const foundContact = getContactById(contact.id, contactList)
    if (!foundContact) return initials
    const { firstName, lastName, type } = foundContact.contactDetails
    const label = `${firstName || ''} ${lastName || ''}`
    initials = getInitials(label, type)
    return initials
  }

  const userContacts = commContact.find(
    (c) => c.type === 'User' && c.id !== userId
  )
  if (userContacts) initials = getInitials(userContacts.displayName, 'User')

  return initials
}

export const getContactByNumber = async (token: string, number: string) => {
  try {
    const res = await HTTP.get(`/contact_ph/${number}`, {
      headers: {
        Authorization: 'Bearer ' + token
      }
    })
    const { contacts } = res.data
    return contacts[0]
  } catch (error) {
    return null
  }
}

export const filterContactsByAlphabet = (
  contacts: Array<any>,
  filter: string
): { [key: string]: Array<any> } => {
  const filteredContacts = contacts
    .filter((contact: any) => {
      const { firstName, lastName, phoneNumbers } = contact.contactDetails

      let contactStrings = `  ${firstName.toLowerCase()}  ${lastName.toLowerCase()}  `

      for (const number of phoneNumbers) {
        if (number && number.number) {
          contactStrings = contactStrings.concat(`  ${number.number}  `)
        }
      }

      if (filter) {
        const searchStrings = filter.toLowerCase().trim().split(' ')
        return searchStrings.every((searchString) => {
          return contactStrings.includes(searchString)
        })
      }

      return true
    })
    .sort((a, b) =>
      a.contactDetails.firstName
        .toLowerCase()
        .localeCompare(b.contactDetails.firstName.toLowerCase())
    )

  const alphabetMap = {}

  filteredContacts.forEach((contact) => {
    const referenceName: string =
      contact.contactDetails.firstName || contact.contactDetails.lastName
    const firstLetter = referenceName[0].toUpperCase()

    if (!alphabetMap[firstLetter]) {
      alphabetMap[firstLetter] = []
    }

    alphabetMap[firstLetter].push(contact)
  })

  const sortedAlphabetmap = Object.entries(alphabetMap)
    .sort((a, b) => {
      const [key1] = a
      const [key2] = b

      return key1.localeCompare(key2)
    })
    .reduce((prev, curr) => {
      const [letter, value] = curr
      prev[letter] = value

      return prev
    }, {})

  return sortedAlphabetmap
}

export const filterContacts = (
  contacts: Array<any>,
  filter: string,
  phoneNumbersOnly = false
) => {
  return contacts
    .filter((contact: any) => {
      const { firstName, lastName, phoneNumbers, defaultPhoneNumber } =
        contact.contactDetails
      if (phoneNumbersOnly) {
        if (!defaultPhoneNumber && !phoneNumbers.length) return false
      }
      let contactStrings = `  ${firstName.toLowerCase()}  ${lastName.toLowerCase()}  `
      for (const number of phoneNumbers) {
        if (number && number.number) {
          contactStrings = contactStrings.concat(`  ${number.number}  `)
        }
      }
      if (filter) {
        const searchStrings = filter.toLowerCase().trim().split(' ')
        return searchStrings.every((searchString) => {
          return contactStrings.includes(searchString)
        })
      }
      return true
    })
    .sort((a, b) =>
      a.contactDetails.firstName
        .toLowerCase()
        .localeCompare(b.contactDetails.firstName.toLowerCase())
    )
}

export const filterUsers = (users: Array<any>, filter: string) => {
  return users.filter((user: any) => {
    const { firstName, lastName, extension } = user
    const contactStrings = `  ${firstName ? firstName.toLowerCase() : ''}  ${
      lastName ? lastName.toLowerCase() : ''
    }  ${extension || ''}`
    if (filter) {
      const searchStrings = filter.toLowerCase().trim().split(' ')
      return searchStrings.every((searchString) => {
        return contactStrings.includes(searchString)
      })
    }
    return true
  })
}

export const linkContactSettings = (
  settings: ContactSettings,
  templates: { [key: string]: ContactSettings }
): ContactSettings => {
  if (!settings || !templates) {
    return undefined
  }

  const linkedSettings: any = {}
  const settingsCopy = { ...settings }

  Object.keys(settings).forEach((key) => {
    if (
      settings[key] === 'true' ||
      settings[key] === 'false' ||
      settings[key] === 'unknown' ||
      typeof settings[key] === 'object'
    ) {
      linkedSettings[key] = settings[key]
    } else {
      const templateId: string = settings[key]

      // hack for now to have matterId correctly linked
      if (templateId.includes('-')) {
        linkedSettings[key] = templateId
      } else {
        const template = templates[templateId]
        linkedSettings[key] = template[key]
        settingsCopy[key] = template[key]
      }
    }

    if (key === 'smsReadSettings' || key === 'smsWriteSettings') {
      linkedSettings[key] = {
        durationType: settingsCopy[key].durationType,
        rate: settingsCopy[key].rate
      }
    }
  })

  return linkedSettings
}

export const sanitizeContactSettingsForm = (
  settings: any,
  pointer?: string
) => {
  if (!settings) {
    return undefined
  }

  const sanitizedFormValues = cloneDeep(settings)

  if (pointer) {
    Object.keys(sanitizedFormValues).forEach((key) => {
      sanitizedFormValues[key] = pointer
    })
  } else {
    if (typeof sanitizedFormValues.callCommunicationMatter === 'object') {
      sanitizedFormValues.callCommunicationMatter =
        sanitizedFormValues.callCommunicationMatter.matterId
    }
    if (typeof sanitizedFormValues.smsCommunicationMatter === 'object') {
      sanitizedFormValues.smsCommunicationMatter =
        sanitizedFormValues.smsCommunicationMatter.matterId
    }
    if (
      typeof sanitizedFormValues.smsConversationCommunicationMatter === 'object'
    ) {
      sanitizedFormValues.smsConversationCommunicationMatter =
        sanitizedFormValues.smsConversationCommunicationMatter.matterId
    }

    if (!sanitizedFormValues.smsReadSettings.rate) {
      sanitizedFormValues.smsReadSettings.rate = 0
    } else {
      sanitizedFormValues.smsReadSettings.rate = settings.smsReadSettings.rate
    }

    if (!sanitizedFormValues.smsWriteSettings.rate) {
      sanitizedFormValues.smsWriteSettings.rate = 0
    } else {
      sanitizedFormValues.smsWriteSettings.rate = settings.smsWriteSettings.rate
    }
  }

  return sanitizedFormValues
}
